import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export const CHECKLIST_PROPOSED_POLICIES = Object.freeze([
  ['benchmarks.product_cost_pct_min', 0.08, 'pct', '8%'],
  ['benchmarks.product_cost_pct_max', 0.10, 'pct', '10%'],
  ['benchmarks.fulfillment_pct_min', 0.04, 'pct', '4%'],
  ['benchmarks.fulfillment_pct_max', 0.06, 'pct', '6%'],
  ['benchmarks.merchant_fees_pct_min', 0.045, 'pct', '4.5%'],
  ['benchmarks.merchant_fees_pct_max', 0.065, 'pct', '6.5%'],
  ['inventory.cogs_variance_pct_tolerance', 0.10, 'pct', '10%'],
  ['revenue.kpi_gap_pct_tolerance', 0.05, 'pct', '5%'],
  ['revenue.tracker_gap_monthly_pct', 0.05, 'pct', '5%'],
  ['revenue.tracker_gap_ytd_pct', 0.02, 'pct', '2%'],
  ['revenue.tracker_driver_abs_usd', 15000, 'usd', '$15,000'],
].map(([policy_key, value, unit, human_value]) => Object.freeze({
  policy_key, value, unit, human_value,
})));

const currency = (value) => {
  if (value === null || value === undefined) return '—';
  const amount = Number(value);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Math.abs(amount));
  return amount < 0 ? `(${formatted})` : formatted;
};

const stopSummaryToggle = (children) => React.Children.map(children, (child) => {
  if (!React.isValidElement(child)) return child;
  const nestedChildren = child.props.children
    ? stopSummaryToggle(child.props.children)
    : child.props.children;
  if (child.type !== 'button') {
    return nestedChildren === child.props.children
      ? child
      : React.cloneElement(child, {}, nestedChildren);
  }
  return React.cloneElement(child, {
    onClick: (event) => {
      event.stopPropagation();
      child.props.onClick?.(event);
    },
  }, nestedChildren);
});

export const StatusBadge = ({ status, label }) => {
  if (!status) return null;
  const tone = status === 'PASS'
    ? 'bg-green-100 text-green-800 font-bold'
    : status === 'PENDING_REVIEW'
      ? 'bg-amber-100 text-amber-800'
      : 'bg-red-100 text-red-800';
  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${tone}`}>
      {label || (status === 'PENDING_REVIEW' ? 'Pending review' : status)}
    </span>
  );
};

export const SupportingScheduleBadge = ({ conclusion }) => {
  const tone = conclusion === 'tied'
    ? 'bg-green-100 text-green-800'
    : ['link_missing', 'not_found', 'qbo_unavailable'].includes(conclusion)
      ? 'bg-amber-100 text-amber-800'
    : conclusion === 'not_tied'
      ? 'bg-red-100 text-red-800'
      : 'bg-red-100 text-red-800';
  const label = {
    tied: 'Tied',
    not_tied: 'Not tied',
    inaccessible: 'Inaccessible',
    link_missing: 'Link missing',
    not_found: 'Not found',
    qbo_unavailable: 'QBO unavailable',
  }[conclusion] || conclusion || 'Unknown';
  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${tone}`}>
      {label}
    </span>
  );
};

export const CollapsibleSection = ({
  title,
  subtitle,
  status,
  actions,
  defaultOpen = false,
  children,
  className = '',
  id,
}) => (
    <details id={id} open={defaultOpen} className={`min-w-0 rounded-[10px] border border-[#ececef] bg-white p-4 ${className}`}>
      <summary className="flex cursor-pointer list-none flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="w-full min-w-0 flex-1 break-words">
          <h2 className="break-words text-[15px] font-[650] tracking-[-.01em] text-[#0f1115]">{title}</h2>
          {subtitle && <p className="text-[12.5px] text-[#7b8090]">{subtitle}</p>}
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        <StatusBadge status={status} />
        {actions && <div className="flex flex-wrap items-center gap-2">{stopSummaryToggle(actions)}</div>}
      </div>
    </summary>
    <div className="mt-4">{children}</div>
  </details>
);

export const Money = ({ value }) => {
  const amount = value === null || value === undefined ? null : Number(value);
  return (
    <span className={amount < 0 ? 'text-red-600' : undefined}>
      {currency(value)}
    </span>
  );
};

export const TieValue = ({ value, tolerance }) => {
  const amount = value === null || value === undefined ? null : Number(value);
  const limit = tolerance === null || tolerance === undefined ? 0.005 : Number(tolerance);
  const withinTolerance = amount !== null && Math.abs(amount) <= limit;
  return (
    <span className={`font-bold ${withinTolerance ? 'text-green-700' : 'text-red-700'}`}>
      {currency(value)}
    </span>
  );
};

export const isEmptyRow = (values) => values.every((value) => (
  value === null
  || value === undefined
  || value === ''
  || value === 'N/A'
  || (Number.isFinite(Number(value)) && Number(value) === 0)
));

export const exceptionTone = (exception, status) => {
  if (['resolved', 'accepted'].includes(exception?.status)) return 'text-slate-500';
  if (!status || status === 'PENDING_REVIEW') return 'text-amber-800';
  return status === 'PASS' ? 'text-green-800' : 'text-red-800';
};

const STATUS_CHIPS = {
  PASS: { label: 'Passed', tone: 'bg-[#ecfdf5] text-[#065f46]', dot: 'bg-[#10b981]' },
  PENDING_REVIEW: { label: 'Needs review', tone: 'bg-[#fff7ed] text-[#c2410c]', dot: 'bg-[#fb923c]' },
  FAIL: { label: 'Failed', tone: 'bg-[#fef2f2] text-[#b91c1c]', dot: 'bg-[#ef4444]' },
};

export const StatusChip = ({ status, label }) => {
  const chip = status
    ? STATUS_CHIPS[status] || { label: status, tone: 'bg-[#fef2f2] text-[#b91c1c]', dot: 'bg-[#ef4444]' }
    : { label: 'Not run', tone: 'bg-white text-[#4b5060] shadow-[inset_0_0_0_1px_#ececef]', dot: 'bg-white shadow-[inset_0_0_0_1.5px_#a2a6b3]' };
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded px-2 py-0.5 text-xs font-[550] ${chip.tone}`}>
      <span aria-hidden="true" className={`inline-block h-[7px] w-[7px] rounded-full ${chip.dot}`} />
      {label || chip.label}
    </span>
  );
};

export const GateTile = ({ children, large = false }) => (
  <span className={`flex flex-none items-center justify-center rounded-[7px] border border-[#ececef] bg-white font-[650] text-[#4b5060] ${large ? 'h-9 w-9 text-[13px]' : 'h-7 w-7 text-[11.5px]'}`}>
    {children}
  </span>
);

export const GateDrawer = ({
  open,
  number,
  title,
  subtitle,
  status,
  actions,
  onClose,
  onStep,
  children,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
      const target = event.target;
      const editable = target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName);
      if (!editable && onStep && event.key === 'ArrowRight') onStep(1);
      if (!editable && onStep && event.key === 'ArrowLeft') onStep(-1);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, onStep]);
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-[rgba(15,17,21,.38)] p-0 md:px-4 md:py-[4vh]"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}
    >
      <div role="dialog" aria-modal="true" aria-label={title} className="flex h-full w-full max-w-[1120px] flex-col overflow-hidden bg-white text-[13px] text-[#0f1115] md:h-auto md:max-h-[92vh] md:rounded-xl md:border md:border-[#ececef]">
        <div className="flex flex-none flex-wrap items-center gap-3 border-b border-[#f3f3f5] px-5 py-3.5">
          {number && <GateTile large>{number}</GateTile>}
          <div className="min-w-0 flex-1">
            <h2 className="break-words text-[17px] font-[650] tracking-[-.01em]">{title}</h2>
            {subtitle && <p className="text-[12.5px] text-[#7b8090]">{subtitle}</p>}
          </div>
          <div className="flex w-full flex-wrap items-center gap-1.5 md:ml-auto md:w-auto">
            <StatusChip status={status} />
            {actions}
            {onStep && <>
              <button type="button" aria-label="Previous gate" title="Previous gate (←)" onClick={() => onStep(-1)} className="h-[30px] w-[30px] rounded-[7px] border border-[#ececef] bg-white text-[#4b5060]">‹</button>
              <button type="button" aria-label="Next gate" title="Next gate (→)" onClick={() => onStep(1)} className="h-[30px] w-[30px] rounded-[7px] border border-[#ececef] bg-white text-[#4b5060]">›</button>
            </>}
            <button type="button" aria-label="Close details" onClick={onClose} className="h-8 w-8 rounded-[7px] text-lg text-[#7b8090] hover:bg-[#fafafb]">×</button>
          </div>
        </div>
        <div className="min-w-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export const SegmentedTabs = ({ tabs, value, onChange }) => (
  <div role="tablist" className="flex max-w-full overflow-x-auto rounded-[7px] bg-[#f3f3f5] p-0.5">
    {tabs.map((tab) => (
      <button
        key={tab.key}
        type="button"
        role="tab"
        aria-selected={value === tab.key}
        onClick={() => onChange(tab.key)}
        className={`h-[30px] whitespace-nowrap rounded-md px-2.5 text-[13px] ${value === tab.key ? 'bg-[#e6e6ea] font-semibold text-[#0f1115]' : 'text-[#4b5060]'}`}
      >
        {tab.label}
        {tab.count !== undefined && <span className="ml-1 text-[#a2a6b3]">{tab.count}</span>}
      </button>
    ))}
  </div>
);
